/**
 * Preparação do áudio de voz do chat conforme o provider de destino.
 *
 * CHANGELOG:
 *   RELEASE: 2026-08-28 v3.0.0 - Removido o fallback MP3 no provider Meta. Se a
 *            canonicalização OGG/Opus falhar, o envio é interrompido com erro
 *            para ser tratado na tela do chat (nunca cai para áudio comum).
 *   RELEASE: 2026-08-28 v2.0.0 - Restaura a nota de voz (PTT) na Meta. O blob
 *            gravado passa primeiro pelo canonicalizador OGG/Opus
 *            (src/lib/audio/oggOpus.ts); se o resultado for válido, enviamos
 *            `.ogg` + `audio/ogg` com `voice: true`. O MP3 continua existindo
 *            apenas como fallback quando a canonicalização não é possível.
 *   RELEASE: 2026-08-28 v1.0.0 - Versão inicial. Converte o OGG/Opus gravado
 *            pelo navegador em MP3 (audio/mpeg) SOMENTE quando a inbox de
 *            destino é Meta WABA, evitando o erro 131053 da Cloud API sem
 *            nenhuma transcodificação server-side.
 *
 * Regras:
 *   - Providers não-Meta (WAHA, Z-API, WuzAPI, Notificame) continuam recebendo
 *     o OGG/Opus original, sem qualquer alteração de fluxo.
 *   - Para Meta, o caminho preferencial é `voice_ogg` (nota de voz nativa);
 *     `basic_mp3` é o fallback que mantém o envio funcionando.
 */

import { createLogger } from '@/lib/logger';
import { canonicalizeOggOpus } from '@/lib/audio/oggOpus';

const audioLogger = createLogger('AudioConvert');

/**
 * Providers da Cloud API da Meta que exigem preparação específica.
 * `meta-whatsapp` é o slug canônico cadastrado em produção; `meta-waba`
 * permanece como alias para compatibilidade com registros antigos.
 */
const META_PROVIDERS = new Set(['meta-whatsapp', 'meta-waba']);

/** Bitrate alvo para voz — arquivo enxuto e qualidade adequada */
const VOICE_BITRATE_KBPS = 48;

/** Modo final de preparação do áudio */
export type PreparedAudioMode = 'voice_ogg' | 'basic_mp3';

export interface PreparedAudio {
  // Blob que deve ser enviado/armazenado
  blob: Blob;
  // MIME type real do blob
  mimeType: string;
  // Extensão coerente com o MIME (sem ponto)
  extension: string;
  // Indica se é nota de voz nativa (PTT/waveform)
  isVoiceNote: boolean;
  // Indica se houve conversão de codec (MP3)
  converted: boolean;
  // Modo final aplicado
  mode: PreparedAudioMode;
}

/** Verifica se a inbox é de um provider Meta (Cloud API) */
export function isMetaProvider(providerSlug?: string | null): boolean {
  return !!providerSlug && META_PROVIDERS.has(providerSlug.trim().toLowerCase());
}

/**
 * Mantido por compatibilidade com chamadas existentes: indica que a inbox
 * exige preparação especial de áudio (hoje: providers Meta).
 */
export function providerRequiresMp3(providerSlug?: string | null): boolean {
  return isMetaProvider(providerSlug);
}


/** Confirma que o encoder devolveu um stream MP3 reconhecível. */
function hasValidMp3Signature(buffer: ArrayBuffer): boolean {
  const bytes = new Uint8Array(buffer);
  if (bytes.length < 3) return false;

  // MP3 pode iniciar com tag ID3 ou diretamente com o sync word do frame MPEG.
  const hasId3 = bytes[0] === 0x49 && bytes[1] === 0x44 && bytes[2] === 0x33;
  const hasFrameSync = bytes[0] === 0xff && (bytes[1] & 0xe0) === 0xe0;
  return hasId3 || hasFrameSync;
}

/**
 * Decodifica o blob e devolve as amostras em mono (mixdown dos canais).
 */
async function decodeToMonoPcm(blob: Blob): Promise<{ samples: Float32Array; sampleRate: number }> {
  const arrayBuffer = await blob.arrayBuffer();
  const AudioCtx: typeof AudioContext =
    window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
  const context = new AudioCtx();

  try {
    const decoded = await context.decodeAudioData(arrayBuffer.slice(0));

    // Mixdown para mono (média dos canais) — padrão de mensagem de voz
    const channels = decoded.numberOfChannels;
    const length = decoded.length;
    const mono = new Float32Array(length);

    if (channels === 1) {
      mono.set(decoded.getChannelData(0));
    } else {
      for (let c = 0; c < channels; c++) {
        const data = decoded.getChannelData(c);
        for (let i = 0; i < length; i++) mono[i] += data[i] / channels;
      }
    }

    return { samples: mono, sampleRate: decoded.sampleRate };
  } finally {
    // Libera o contexto de áudio (evita vazamento de recursos no navegador)
    void context.close();
  }
}

/**
 * Executa a codificação MP3 dentro de um Web Worker (fora da thread de UI).
 */
function encodeInWorker(samples: Float32Array, sampleRate: number): Promise<ArrayBuffer> {
  return new Promise((resolve, reject) => {
    const worker = new Worker(new URL('./mp3Encoder.worker.ts', import.meta.url), { type: 'module' });

    worker.onmessage = (event: MessageEvent<{ ok: boolean; buffer?: ArrayBuffer; error?: string }>) => {
      worker.terminate();
      if (event.data.ok && event.data.buffer) resolve(event.data.buffer);
      else reject(new Error(event.data.error || 'Falha desconhecida no encoder MP3'));
    };

    worker.onerror = (event) => {
      worker.terminate();
      reject(new Error(event.message || 'Erro no worker de conversão MP3'));
    };

    // Transfere o buffer para o worker (zero-copy)
    worker.postMessage(
      { samples, sampleRate, bitrateKbps: VOICE_BITRATE_KBPS },
      [samples.buffer],
    );
  });
}

/**
 * Converte o blob gravado em MP3 (fallback de áudio básico).
 */
async function encodeToMp3(audioBlob: Blob): Promise<PreparedAudio> {
  const startedAt = performance.now();
  const { samples, sampleRate } = await decodeToMonoPcm(audioBlob);
  const mp3Buffer = await encodeInWorker(samples, sampleRate);

  if (!mp3Buffer.byteLength) throw new Error('Encoder retornou buffer vazio');
  if (!hasValidMp3Signature(mp3Buffer)) {
    throw new Error('Encoder retornou um arquivo sem assinatura MP3 válida');
  }

  audioLogger.info('Áudio convertido para MP3 (fallback de áudio básico)', {
    originalBytes: audioBlob.size,
    mp3Bytes: mp3Buffer.byteLength,
    sampleRate,
    elapsedMs: Math.round(performance.now() - startedAt),
  });

  return {
    blob: new Blob([mp3Buffer], { type: 'audio/mpeg' }),
    mimeType: 'audio/mpeg',
    extension: 'mp3',
    isVoiceNote: false,
    converted: true,
    mode: 'basic_mp3',
  };
}

/**
 * Prepara o áudio de voz para o provider de destino.
 *
 * Meta: SOMENTE `voice_ogg` (nota de voz nativa com waveform). Se a
 * canonicalização do container falhar, o envio é interrompido com erro claro —
 * não existe mais fallback para MP3 (v3.0.0).
 * Demais providers: mantém o OGG/Opus original, sem alteração de comportamento.
 *
 * @param audioBlob Blob gravado (OGG/Opus na maioria dos casos)
 * @param providerSlug Slug do provider da inbox (ex.: 'meta-whatsapp', 'waha', 'z-api')
 */
export async function prepareAudioForProvider(
  audioBlob: Blob,
  providerSlug?: string | null,
): Promise<PreparedAudio> {
  // Caminho padrão dos providers não-Meta: OGG/Opus original intocado
  const original: PreparedAudio = {
    blob: audioBlob,
    mimeType: 'audio/ogg',
    extension: 'ogg',
    isVoiceNote: true,
    converted: false,
    mode: 'voice_ogg',
  };

  if (!isMetaProvider(providerSlug)) return original;

  // Meta: único caminho válido é o OGG/Opus canônico como nota de voz
  try {
    const startedAt = performance.now();
    const source = new Uint8Array(await audioBlob.arrayBuffer());
    const canonical = canonicalizeOggOpus(source);

    audioLogger.info('Áudio preparado como nota de voz (Meta)', {
      originalPreSkip: canonical.originalPreSkip,
      finalPreSkip: canonical.finalPreSkip,
      patched: canonical.patched,
      pages: canonical.info.pageCount,
      bytes: canonical.bytes.byteLength,
      elapsedMs: Math.round(performance.now() - startedAt),
    });

    return {
      blob: new Blob([canonical.bytes], { type: 'audio/ogg' }),
      mimeType: 'audio/ogg',
      extension: 'ogg',
      isVoiceNote: true,
      converted: false,
      mode: 'voice_ogg',
    };
  } catch (canonicalError) {
    // Sem fallback MP3: falha explícita para ser tratada na tela do chat.
    audioLogger.error('Falha ao canonicalizar OGG/Opus para a Meta', canonicalError);
    throw new Error(
      'Não foi possível preparar o áudio como nota de voz para o WhatsApp oficial. Grave novamente.',
    );
  }
}


